import { useState, useEffect, useCallback } from "react";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import AuthHandler from "@/components/admin/AuthHandler";
import ProjectManager from "@/components/admin/ProjectManager";
import ReviewManager from "@/components/admin/ReviewManager";
import ContactManager from "@/components/admin/ContactManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session storage keys
const TOKEN_KEY = "adminToken";
const AUTH_KEY = "admin_auth"; // Keep for backward compatibility

const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [token, setToken] = useState(null);
  const [credentials, setCredentials] = useState({ username: "", password: "" }); // Keep for backward compat
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("projects");

  // Create axios instance with auth header
  const getAuthConfig = useCallback((authToken) => {
    if (authToken) {
      return { headers: { Authorization: `Bearer ${authToken}` } };
    }
    // Fallback to basic auth for legacy support
    if (credentials.username && credentials.password) {
      return { auth: { username: credentials.username, password: credentials.password } };
    }
    return {};
  }, [credentials]);

  const fetchData = useCallback(async (authToken, creds) => {
    try {
      const config = authToken 
        ? { headers: { Authorization: `Bearer ${authToken}` } }
        : { auth: { username: creds?.username, password: creds?.password } };
      
      const [projectsRes, contactsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/contacts`, config),
        axios.get(`${API}/reviews/all`, config),
      ]);
      setProjects(projectsRes.data);
      setContacts(contactsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        // Clear session on auth failure
        setIsLoggedIn(false);
        setToken(null);
        setCredentials({ username: "", password: "" });
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(AUTH_KEY);
        toast.error("Session expired. Please login again.");
      }
    }
  }, []);

  const handleLogout = useCallback(() => {
    setIsLoggedIn(false);
    setToken(null);
    setCredentials({ username: "", password: "" });
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(AUTH_KEY);
    toast.success("Logged out");
  }, []);

  useEffect(() => {
    const verifyAndLoadData = async () => {
      // Try JWT token first
      const storedToken = sessionStorage.getItem(TOKEN_KEY);
      if (storedToken) {
        try {
          // Verify token is still valid
          await axios.get(`${API}/admin/verify`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setIsLoggedIn(true);
          setToken(storedToken);
          fetchData(storedToken, null);
        } catch (error) {
          // Only clear token on 401 (unauthorized), not on network errors
          if (error.response?.status === 401) {
            sessionStorage.removeItem(TOKEN_KEY);
            sessionStorage.removeItem(AUTH_KEY);
          } else {
            // Network error - still try to use the token
            setIsLoggedIn(true);
            setToken(storedToken);
            fetchData(storedToken, null);
          }
        }
        return;
      }
      
      // Fallback to legacy auth
      const auth = sessionStorage.getItem(AUTH_KEY);
      if (auth) {
        try {
          const parsed = JSON.parse(auth);
          setIsLoggedIn(true);
          setCredentials(parsed);
          fetchData(null, parsed);
        } catch {
          sessionStorage.removeItem(AUTH_KEY);
        }
      }
    };
    
    verifyAndLoadData();
  }, [fetchData]);

  const handleLogin = async (creds) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, creds);
      const accessToken = response.data.access_token;
      
      setIsLoggedIn(true);
      setToken(accessToken);
      setCredentials(creds); // Keep for backward compat
      sessionStorage.setItem(TOKEN_KEY, accessToken);
      toast.success("Login successful!");
      fetchData(accessToken, null);
    } catch {
      toast.error("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData(token, credentials);
  };

  if (!isLoggedIn) {
    return <AuthHandler onLogin={handleLogin} loading={loading} />;
  }

  return (
    <div className="min-h-screen pt-32 pb-16 bg-[#F5F5F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1
            className="text-3xl font-black text-[#0A0A0A]"
            style={{ fontFamily: "'Chivo', sans-serif" }}
          >
            ADMIN <span className="text-[#FF6B00]">DASHBOARD</span>
          </h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#525252] hover:text-[#FF6B00] transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E5E5E5] mb-8">
          {[
            { id: "projects", label: "Projects", count: projects.length },
            { id: "contacts", label: "Enquiries", count: contacts.length },
            { id: "reviews", label: "Reviews", count: reviews.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 font-bold uppercase text-sm tracking-wide transition-colors ${
                activeTab === tab.id
                  ? "text-[#FF6B00] border-b-2 border-[#FF6B00]"
                  : "text-[#525252] hover:text-[#0A0A0A]"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="bg-white border border-[#E5E5E5] p-6">
          {activeTab === "projects" && (
            <ProjectManager
              projects={projects}
              token={token}
              credentials={credentials}
              onRefresh={handleRefresh}
            />
          )}
          {activeTab === "contacts" && <ContactManager contacts={contacts} />}
          {activeTab === "reviews" && (
            <ReviewManager
              reviews={reviews}
              token={token}
              credentials={credentials}
              onRefresh={handleRefresh}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
